import { useEffect, useState } from "react";
import type { ApiContract } from "@proof/contracts";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";
import { downloadBlob } from "./api";

const shell = (text: string) => `'${text.replaceAll("'", "'\\''")}'`;
export function ApiReference({
  notify,
}: {
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport();
  const [state, setState] = useState<{
    transport: typeof transport;
    contract: ApiContract;
  }>();
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, select] = useState("jobs.create");
  const [schemaName, setSchemaName] = useState("ProjectInput");
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    void transport.client
      .apiContract(controller.signal)
      .then((contract) => {
        if (!controller.signal.aborted) setState({ transport, contract });
      })
      .catch((failure: Error) => {
        if (!controller.signal.aborted) setError(failure.message);
      });
    return () => controller.abort();
  }, [transport, refresh]);
  const contract = state?.transport === transport ? state.contract : undefined;
  const operations = contract
    ? Object.entries(contract.paths).flatMap(([path, methods]) =>
        Object.entries(methods).map(([method, operation]) => ({
          path,
          method: method.toUpperCase(),
          operation,
        })),
      )
    : [];
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = operations.filter(({ path, method, operation }) =>
    terms.every((term) =>
      `${path} ${method} ${operation.operationId} ${operation.summary} ${operation.tags.join(" ")}`
        .toLowerCase()
        .includes(term),
    ),
  );
  const chosen =
    filtered.find((item) => item.operation.operationId === selectedId) ||
    filtered[0];
  const curl = chosen
    ? [
        `curl --globoff --request ${chosen.method} ${shell(`${new URL(transport.client.baseUrl || "/", window.location.origin).href.replace(/\/+$/, "")}${chosen.path}`)}`,
        ...(contract?.["x-proof-authentication"] === "demo"
          ? []
          : [`  --header 'Authorization: Bearer <host-token>'`]),
        ...(chosen.operation.requestBody
          ? [
              `  --header ${shell(`Content-Type: ${Object.keys(chosen.operation.requestBody.content)[0]}`)}`,
              Object.keys(chosen.operation.requestBody.content).includes(
                "application/json",
              )
                ? "  --data-binary '@request.json'"
                : "  --header 'X-Filename: upload.bin' --data-binary '@upload.bin'",
            ]
          : []),
      ].join(" \\\n")
    : "";
  return (
    <section
      className="panel padded api-reference"
      aria-label="Native API reference"
    >
      <div className="section-heading">
        <h2>Native API reference</h2>
        <div className="button-row">
          <Button onClick={() => setRefresh((value) => value + 1)}>
            Refresh API contract
          </Button>
          <Button
            disabled={!contract}
            onClick={() => {
              if (contract)
                downloadBlob(
                  new Blob([JSON.stringify(contract, null, 2) + "\n"], {
                    type: "application/json",
                  }),
                  `proof-studio-api-${contract["x-proof-mode"]}.json`,
                );
            }}
          >
            Download OpenAPI JSON
          </Button>
        </div>
      </div>
      <p>
        Use this contract with your HTTP tooling or client generator in another
        language. Each operation describes its inputs, responses, capabilities
        and current server mode.
      </p>
      {error && <p role="alert">{error}</p>}
      {!contract && !error && <p role="status">Loading API contract…</p>}
      {contract && (
        <>
          <p className="note-box">
            OpenAPI {contract.openapi} · API {contract.info.version} ·{" "}
            {operations.length} tenant operations ·{" "}
            {contract["x-proof-mode"] === "demo"
              ? "Database-free demo: inline generation snapshots are allowed."
              : "Configured host: generation requires a published version."}
          </p>
          <Control label="Search API operations">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find generation, datasets, proofs…"
            />
          </Control>
          <Control label="API operation">
            <select
              value={chosen?.operation.operationId || ""}
              onChange={(event) => select(event.target.value)}
            >
              {filtered.map(({ path, method, operation }) => (
                <option
                  key={operation.operationId}
                  value={operation.operationId}
                >
                  {method} {path} · {operation.summary}
                </option>
              ))}
            </select>
          </Control>
          {!chosen && <p role="status">No API operations match this search.</p>}
          {chosen && (
            <article aria-label="Selected API operation">
              <h3>{chosen.operation.summary}</h3>
              <code>
                {chosen.method} {chosen.path}
              </code>
              <p>{chosen.operation.description}</p>
              <p>
                Required capabilities:{" "}
                {chosen.operation["x-proof-capabilities"].length
                  ? chosen.operation["x-proof-capabilities"]
                      .map((clause) => `(${clause.join(" OR ")})`)
                      .join(" AND ")
                  : contract["x-proof-authentication"] === "demo"
                    ? "Demo identity"
                    : "Authenticated tenant access"}
                . Rate bucket: {chosen.operation["x-proof-rate-limit"]}.
              </p>
              {chosen.operation["x-proof-demo-only"] && (
                <p className="note-box">
                  Available only in the database-free demo.
                </p>
              )}
              {!!chosen.operation.parameters.length && (
                <details>
                  <summary>Path, query and header parameters</summary>
                  <pre>
                    {JSON.stringify(chosen.operation.parameters, null, 2)}
                  </pre>
                </details>
              )}
              {chosen.operation.requestBody && (
                <details open>
                  <summary>Request body</summary>
                  <pre>
                    {JSON.stringify(chosen.operation.requestBody, null, 2)}
                  </pre>
                </details>
              )}
              <details>
                <summary>Responses and download headers</summary>
                <pre>{JSON.stringify(chosen.operation.responses, null, 2)}</pre>
              </details>
              <details>
                <summary>HTTP command example</summary>
                <p>
                  Replace path placeholders and use your host token. For a body,
                  prepare the named file using the request schema. Binary
                  requests use the matching MIME type and original filename.
                  Operator support sessions use the same-origin session flow
                  instead.
                </p>
                <pre>{curl}</pre>
                <Button
                  onClick={() => {
                    if (!navigator.clipboard?.writeText) {
                      notify("Copy the command from the example above.");
                      return;
                    }
                    void navigator.clipboard
                      .writeText(curl)
                      .then(() => notify("HTTP command copied."))
                      .catch(() =>
                        notify("Copy the command from the example above."),
                      );
                  }}
                >
                  Copy HTTP command
                </Button>
              </details>
            </article>
          )}
          <details>
            <summary>Browse shared data schemas</summary>
            <Control label="API data schema">
              <select
                value={schemaName}
                onChange={(event) => setSchemaName(event.target.value)}
              >
                {Object.keys(contract.components.schemas)
                  .sort()
                  .map((name) => (
                    <option key={name}>{name}</option>
                  ))}
              </select>
            </Control>
            <pre>
              {JSON.stringify(contract.components.schemas[schemaName], null, 2)}
            </pre>
          </details>
          <p className="muted">
            Identity and trusted context come from your host. The reference does
            not execute requests or include your credentials. Operator sign-in
            and instance administration are separate interfaces. Retained
            approval does not commit an order; generated output remains
            pressReady=false.
          </p>
        </>
      )}
    </section>
  );
}
